import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { PieChartComponent } from '../../shared/reusables/pie-chart/pie-chart.component';
import { StatisticCardsComponent } from '../../shared/reusables/statistic-cards/statistic-cards.component';
import { BarChartComponent } from '../../shared/reusables/bar-chart/bar-chart.component';
import { AlertComponent } from '../../shared/reusables/alert/alert.component';
import { ToastService } from '../../shared/services/toast.service';
import { AuthService } from '../../shared/services';
import { ChartOptions, ChartData } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MobileNavService } from '../../shared/mobile/mobile-bottom-nav/mobile-nav.service';
import { MobileInventoryViewComponent } from './mobile-inventory-view/mobile-inventory-view.component';
import { TooltipComponent } from '../../shared/tooltip/tooltip.component';
import { AreaQuantityInputComponent } from '../../shared/area-quantity-input/area-quantity-input.component';
import { SingleSelectDropdownComponent } from '../business-details/single-select-dropdown.component';
import { OffcanvasSheetComponent } from '../shared/offcanvas-sheet/offcanvas-sheet.component';
import { ExcelService } from '../../shared/services/excel.service';
import { parseMonetary } from '../../shared/services/price-parser';
import { NumericInputDirective } from '../../shared/directives/numeric-input.directive';
import * as bootstrap from 'bootstrap';
import { jwtDecode } from 'jwt-decode';

export interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  description?: string;
  itemTypeID: number;
  purchaseUOMID: number;
  lowStockThreshold?: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  sellingPrice: number;
  profitMarginPercent: number;
  purchasePrice: number;
  unitCost?: number;
  valuationMethodCode?: string;
  categoryID: number;
  purchaseQuantity: number;
  supplierID: number;
}

interface BOMComponents {
  bomComponentID: number;
  bomHeaderID: number;
  rawMaterialItemID: number;
  quantityRequired: number;
  componentUOMID: number;
  scrapFactor: number;
  notes: string;
}
interface BOMHeaders {
  bomHeaderID: number;
  finishedGoodItemID: number;
  bomVersion: string;
  description: string;
  isActive: boolean;
  validFrom: string;
  validTo: string;
  createdAt: string;
  userIDCreated: number;
  finishedGoodItem: any;
  createdByUser: any;
  components?: BOMComponents[];
  expanded?: boolean;
}
export interface EnhancedItemDTO extends ItemDTO {
  CategoryName: string;
  stockLevel?: number;
  quantity?: number;
  averageCost?: number;
  unitCost?: number;
}

export interface SupplierLocationDTO {
  locationID: number;
  supplierID: number;
  branchName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  locationContactPerson: string;
  locationEmail: string;
  locationPhone: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SupplierDTO {
  supplierID: number;
  companyName: string;
  primaryContactPerson: string;
  primaryEmail: string;
  primaryPhone: string;
  website: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  locations?: SupplierLocationDTO[];
}

export interface CategoryDTO {
  categoryId: number;
  categoryName: string;
  createdAt: Date;
}

export interface UnitOfMeasureDTO {
  uomid: number;
  uomName: string;
  uomCode: string;
}

export interface OperationDTO {
  operationID: number;
  operationName: string;
  operationDescription?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ItemOperationDTO {
  itemOperationID: number;
  finishedGoodItemID: number;
  operationID: number;
  sequenceNumber: number;
  standardSetupTimeMinutes?: number;
  standardRunTimeMinutesPerUnit?: number;
  labourRateIDDefault?: number;
  activityIDDefault?: number;
  notes?: string;
  labourRateID?: number;
  activityID?: number;
  operationName?: string;
}

export interface InventoryTransactionDTO {
  transactionID?: number;
  itemID: number;
  warehouseID: number;
  transactionType: string;
  quantityChange: number;
  lowStockThreshold: number;
  costPerUnit?: number;
  transactionDate: Date;
  referenceType?: string;
  referenceID?: string;
  userID: number;
  notes?: string;
  batchNumber?: string | null;
  expiryDate?: Date | null;
  referenceTypeCode?: string;
}

export interface ItemSupplierPriceHistoryDTO {
  itemSupplierPriceID: number;
  itemID: number;
  supplierLocationID: number;
  purchasePrice: number;
  purchaseQuantity: number;
  currencyCode: string;
  purchaseUOMID: number;
  uomConversionFactor: number;
  supplierItemCode: string;
  leadTimeDays: number;
  validFrom: string;
  validTo?: string;
  createdAt: string;
  createdBy: string;
}

interface ItemTypes {
  itemTypeID: number;
  typeName: string;
}
type SortableInventoryKeys = keyof EnhancedItemDTO | 'CategoryName' | 'purchaseUOMID' | 'purchasePrice' | 'sellingPrice' | 'itemCode' | 'itemName' | 'unitCost';
type FinishedSortableKeys = keyof Pick<EnhancedItemDTO, 'itemCode' | 'itemName' | 'purchasePrice' | 'sellingPrice' | 'purchaseUOMID' | 'CategoryName'>;

@Component({
  selector: 'app-inventory-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgChartsModule,
    PieChartComponent,
    BarChartComponent,
    AlertComponent,
    RouterModule,
    TooltipComponent,
    MobileInventoryViewComponent,
    AreaQuantityInputComponent,
    SingleSelectDropdownComponent,
    OffcanvasSheetComponent,
    NumericInputDirective,
  ],
  templateUrl: './inventory-dashboard.component.html',
  styleUrls: [
    '../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './inventory-dashboard.component.css',
  ],
})
export class InventoryDashboardComponent {
  readonly String = String;

  categoryLabels: string[] = [];
  categoryData: number[] = [];
  selectedDatasetLabel: string = 'All';
  rawMaterialsArray = '';
  rawMaterials: EnhancedItemDTO[] = [];
  originalRawMaterials: EnhancedItemDTO[] = [];
  finishedGoods: EnhancedItemDTO[] = [];
  originalFinishedGoods: EnhancedItemDTO[] = [];
  variableItemType: EnhancedItemDTO[] = [];
  animateClickRaw = false;
  animateClickFinished = false;
  totalStockItems = 0;
  totalRawMaterials = 0;
  totalFinishedGoods = 0;
  lowStockItems = 0;
  totalRawMaterialValue: number = 0;
  totalProductValue: number = 0;
  totalProductValueFromBOM: number = 0;
  isEditing = false;
  rawMaterialSelections: any[] = [];
  selectedItem: EnhancedItemDTO | null = null;
  showAddModal = false;
  categories: CategoryDTO[] = [];
  suppliers: SupplierDTO[] = [];
  uomList: UnitOfMeasureDTO[] = [];
  operations: OperationDTO[] = [];
  showDeleteModal = false;
  NoProductStock = false;
  WithProductStock = false;
  // warehouses: any[] = [];
  selectedSupplier: SupplierDTO | null = null;
  selectedCategory: CategoryDTO | null = null;
  // selectedWarehouse: any = null;
  selectedOperations: OperationDTO[] = [];
  isCreatingSupplier = false;
  isCreatingCategory = false;
  isFinishedGood = false;
  totalStockValue: number = 0;
  newInventoryItem: Partial<EnhancedItemDTO> = { itemTypeID: 1 };
  itemOperations: ItemOperationDTO[] = [];
  baseColors = [
    '#007A8F',
    '#6C7A3E',
    '#B0213C',
    '#26284F',
    '#7FBF7F',
    '#F3D250',
    '#2C6B47',
    '#A0D83A',
    '#3C9C8E',
    '#FF6F61',
    '#FF2E17',
  ];
  supplierForm: FormGroup;
  inventoryForm: FormGroup;
  categoryForm: FormGroup;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  isAlertVisible = false;
  alertTitle = '';
  alertMessage = '';
  alertBgColor = 'bg-danger';
  alerttextcolor = '#ffffff';
  stackedBarData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string[];
      borderRadius: number;
      borderWidth: number;
    }[];
  } | null = null;
  stackedBarOptions!: ChartOptions<'bar'>;
  donutData!: ChartData<'doughnut'>;
  donutOptions!: ChartOptions<'doughnut'>;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  currentPage: number = 1;
  totalPages: number = 1;
  ItemsPerPage: number = 10;
  totalItemsCount: number = 0;
  // Function reference passed to bar chart for pagination; arrow preserves 'this'
  goToPage = (page: number) => {
    this.onPageChange(page);
  };
  rawMaterialsCurrentPage: number = 1;
  rawMaterialsPageSize: number = 15;
  finishedGoodsCurrentPage: number = 1;
  finishedGoodsPageSize: number = 15;
  finishedSortColumn: FinishedSortableKeys = 'itemCode';
  finishedSortDirection: 'asc' | 'desc' = 'desc';
  selectedFilter = 'all';
  currentFilter = '';
  filters = [
    { label: 'All', key: 'all' },
    { label: 'Raw Materials', key: 'raw' },
    { label: 'Finished Goods', key: 'finished' },
  ];
  isAddingQuantity = false;
  isUpdatingPurchasePrice = false;
  // Toggle for showing/hiding purchase fields in edit mode
  showPurchaseFields = false;

  // Table layout toggle functionality
  isTablesStacked = false;
  showAddOptionModal = false;
  showItemSelectModal = false;

  // New properties for item search
  rawMaterialsSearchTerm: string = '';
  finishedGoodsSearchTerm: string = '';
  filteredRawMaterials: EnhancedItemDTO[] = [];
  filteredFinishedGoods: EnhancedItemDTO[] = [];
  itemSearchTerm = '';
  filteredItems: EnhancedItemDTO[] = [];
  userRole: string | null = null;
  showLowStockPopup: boolean | null = null;
  lowStockItemsList: any[] = [];
  tenantName: any;
  fullName: any;
  selectedCategoryFilter: number | null = null;
  selectedUOMFilter: number | null = null;
  showLowStockOnly: boolean = false;
  stockCheckResults: any;
  showNonLowStockOnly: boolean = false;
  isLoading: boolean = true;

  // Reorder level prompt for items with threshold = 0
  showReorderPrompt: boolean = false;
  zeroReorderItems: EnhancedItemDTO[] = [];
  reorderEditValues: { [itemID: number]: number } = {};

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public authService: AuthService,
    private excelService: ExcelService,
    private router: Router,
    private route: ActivatedRoute,
    private mobileNavService: MobileNavService,
    private toast: ToastService,
  ) {
    this.supplierForm = this.fb.group({
      // Supplier fields
      companyName: ['', Validators.required],
      primaryContactPerson: ['', Validators.required],
      primaryPhone: ['', Validators.required],
      primaryEmail: ['', [Validators.required, Validators.email]],
      website: [''],

      // Supplier Location fields - mark required fields
      branchName: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      region: [''],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
      locationContactPerson: ['', Validators.required],
      locationEmail: ['', [Validators.required, Validators.email]],
      locationPhone: ['', Validators.required]
    });

    this.inventoryForm = this.fb.group({
      // Basic Item Information
      itemName: ['', Validators.required],
      itemCode: [''], // Auto-generated if not provided

      PurchaseUOMID: [null, [Validators.required]],

      // Stock Information
      quantityToAdd: [0, [Validators.required, Validators.min(0)]],
      currentQuantity: [0],
      lowStockThreshold: [null, [Validators.required, Validators.min(0)]],

      // Cost and Pricing
      purchasePrice: [0, [Validators.required, Validators.min(0)]],
      unitCost: [0], // Removed Validators.required
      profitMarginPercent: [0, [Validators.min(0), Validators.max(100)]],
      sellingPrice: [0, [Validators.min(0)]],

      // Additional Information
      valuationMethodCode: ['FIFO'],
      categoryID: [null, Validators.required],
      itemTypeID: [1, Validators.required], // Default to Raw Material
      supplierID: [null],
      isActive: [true]
    });

    this.categoryForm = this.fb.group({
      categoryName: ['', Validators.required],

    });
  }

  get rawMaterialsControls() {
    return (this.inventoryForm.get('rawMaterials') as any).controls;
  }
  getCategoryColor(categoryName: string): string {
    const index = this.categoryLabels.findIndex(label => label === categoryName);

    if (index !== -1 && index < this.baseColors.length) {
      const hex = this.baseColors[index];
      return this.hexToRgba(hex, 0.7); // 0.7 = 70% opacity
    }

    return 'rgba(204, 204, 204, 0.5)'; // fallback color with opacity
  }
  hexToRgba(hex: string, alpha: number): string {
    // Remove '#' if present
    hex = hex.replace('#', '');

    // Support shorthand (e.g., #abc)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }


  LogOut(): void {
    this.authService.logOut();
  }
  private logFormState(): void {
    if (!environment.production) {
    }
  }

  // Runs the shared price parser over a mapped row's monetary fields.
  // Throws on an unrecoverable value (row is rejected); returns a warning entry
  // for a value that had to be corrected or defaulted to 0 (row still imports,
  // flagged for review). validateImportItems (pre-flight) and processBatch (actual
  // POST path) both call this so they can't drift apart again.
  private sanitiseRowPrices(mappedRow: any): { warnings: string[] } {
    const warnings: string[] = [];
    const priceFields = ['purchasePrice', 'sellingPrice', 'unitCost'];
    priceFields.forEach(field => {
      if (mappedRow[field] !== undefined && mappedRow[field] !== null && mappedRow[field] !== '') {
        const result = parseMonetary(mappedRow[field]);
        if (result.error) {
          throw new Error(`${field}: ${result.error}`);
        }
        if (result.sanitised) {
          warnings.push(`${field}: corrected from "${result.rawValue}" to ${result.value}`);
        }
        mappedRow[field] = result.value;
      }
    });
    return { warnings };
  }

  // Validate the import rows without saving anything. Returns row-level errors and warnings.
  private validateImportItems(items: any[]): { errors: any[]; warnings: any[] } {
    const errors: any[] = [];
    const warnings: any[] = [];
    // Track duplicates by itemCode (case-insensitive)
    const seenCodes = new Set<string>();
    const existingPool: any[] = [
      ...(this.rawMaterials || []),
      ...(this.finishedGoods || []),
      ...(this.originalRawMaterials || []),
      ...(this.originalFinishedGoods || []),
      ...(this.filteredItems || [])
    ];
    const existingCodes = new Set(
      existingPool
        .map(i => (i?.itemCode || '').toLowerCase())
        .filter(c => !!c)
    );

    items.forEach((row: any, index: number) => {
      try {
        // Normalize keys similar to processBatch
        const normalizedRow: any = {};
        Object.keys(row).forEach(key => {
          const normalizedKey = key
            .replace(/\*+$/, '')
            .toLowerCase()
            .replace(/([-_][a-z])/g, group => group.toUpperCase().replace(/[-_]/g, ''));
          const value = row[key];
          normalizedRow[normalizedKey] = (value === '' || value === undefined) ? null : value;
        });

        const itemName = String(normalizedRow.itemName || '').trim();
        const lowerItemName = itemName.toLowerCase();
        const isInstructionRow = (
          lowerItemName === '' ||
          lowerItemName === 'example item' ||
          lowerItemName.startsWith('* required fields') ||
          lowerItemName.startsWith('instructions:') ||
          lowerItemName.startsWith('1. fill in the required fields') ||
          lowerItemName.startsWith('2. select values from dropdowns') ||
          lowerItemName.startsWith('3. do not modify the header') ||
          lowerItemName.startsWith('4. delete the example row') ||
          lowerItemName.startsWith('5. save as .xlsx file') ||
          lowerItemName.includes('example row') ||
          lowerItemName.includes('instruction')
        ) && (
            !normalizedRow.category &&
            !normalizedRow.unitofmeasure &&
            !normalizedRow.purchaseprice
          );

        if (isInstructionRow) {
          return; // skip validation for instruction/example rows
        }

        const fieldMappings: { [key: string]: string } = {
          itemname: 'itemName',
          category: 'category',
          unitofmeasure: 'unitOfMeasure',
          purchaseprice: 'purchasePrice',
          unitcost: 'unitCost',
          purchasequantity: 'purchaseQuantity',
          lowstockthreshold: 'lowStockThreshold',
          sellingprice: 'sellingPrice',
          sku: 'itemCode'
        };

        const mappedRow: any = {};
        Object.keys(normalizedRow).forEach(key => {
          const mappedKey = fieldMappings[key.toLowerCase()] || key;
          mappedRow[mappedKey] = normalizedRow[key];
        });

        // Ensure SKU is available by auto-generating if missing (consistent with processBatch)
        mappedRow.itemCode = String(mappedRow.itemCode || this.generateSKU(mappedRow.itemName));

        // Clean and validate monetary values
        const { warnings: rowWarnings } = this.sanitiseRowPrices(mappedRow);
        if (rowWarnings.length > 0) {
          warnings.push({
            item: row,
            warning: rowWarnings.join('; '),
            rowIndex: row?.__excelRow ?? (index + 1)
          });
        }

        // Determine if this row represents a finished good (product)
        const isFinishedGood = (
          (mappedRow.sellingPrice !== undefined && mappedRow.sellingPrice !== null) &&
          (mappedRow.purchasePrice === undefined && mappedRow.purchaseQuantity === undefined)
        ) || String(normalizedRow.itemtype || '').toLowerCase() === 'finished' || String(normalizedRow.itemtypeid || '').toLowerCase() === '2';

        // Required fields differ for finished goods vs raw materials
        const requiredFields = isFinishedGood
          ? ['itemName', 'category', 'unitOfMeasure', 'sellingPrice']
          : ['itemName', 'category', 'unitOfMeasure', 'purchasePrice', 'purchaseQuantity'];
        const missingFields = requiredFields.filter(field => mappedRow[field] === undefined || mappedRow[field] === null || mappedRow[field] === '');
        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Duplicate checks (by itemCode)
        const codeKey = String((mappedRow.itemCode || '').toLowerCase());
        if (codeKey) {
          if (existingCodes.has(codeKey)) {
            throw new Error(`Duplicate item: itemCode '${mappedRow.itemCode}' already exists in system`);
          }
          if (seenCodes.has(codeKey)) {
            throw new Error(`Duplicate item in file: itemCode '${mappedRow.itemCode}' appears more than once`);
          }
          seenCodes.add(codeKey);
        }

        // Reference lookups
        const category = this.categories.find(c => c.categoryName?.toLowerCase() === String(mappedRow.category || '').toLowerCase());
        const uom = this.uomList.find(u =>
          u.uomName?.toLowerCase() === String(mappedRow.unitOfMeasure || '').toLowerCase() ||
          u.uomName?.toLowerCase() === String(normalizedRow.uom || '').toLowerCase() ||
          u.uomCode?.toLowerCase() === String(mappedRow.unitOfMeasure || '').toLowerCase() ||
          u.uomCode?.toLowerCase() === String(normalizedRow.uom || '').toLowerCase()
        );
        if (!category) {
          throw new Error(`Category not found: ${mappedRow.category}`);
        }
        if (!uom) {
          throw new Error(`Unit of measure not found: ${mappedRow.unitOfMeasure || normalizedRow.uom}`);
        }

        if (!isFinishedGood) {
          const supplier = normalizedRow.supplier
            ? this.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())
            : null;
          if (!supplier) {
            throw new Error('Supplier is required for raw materials');
          }
        }

      } catch (err: any) {
        errors.push({
          item: row,
          error: err?.message || 'Unknown validation error',
          rowIndex: row?.__excelRow ?? (index + 1)
        });
      }
    });

    return { errors, warnings };
  }

  // Forcefully hide a Bootstrap modal element if needed (DOM fallback)
  private forceHideModalElement(elementId: string): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.classList.remove('show');
    (el as HTMLElement).style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    // Remove any lingering backdrops just in case
    document.querySelectorAll('.modal-backdrop').forEach(b => b.parentElement?.removeChild(b));
    // Ensure body state is reset
    document.body.classList.remove('modal-open');
    (document.body as HTMLElement).style.removeProperty('padding-right');
  }

  // Unified helper to close and dispose a Bootstrap modal instance and ensure DOM cleanup
  private closeAndDisposeModal(modalInstance: any, elementId: string): void {
    try { modalInstance?.hide?.(); } catch { }
    try { modalInstance?.dispose?.(); } catch { }
    this.forceHideModalElement(elementId);
  }

  private loadUnitsOfMeasure(): void {
    this.loadSupplierPriceHistory();
    const token = localStorage.getItem('authToken');
    if (!token) {
      // console.error('Auth token is missing');
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const url = `${environment.apiUrl}/api/UnitOfMeasure/GetAll`;
    this.http.get<any[]>(url, { headers }).subscribe({
      next: (uoms) => {
        this.uomList = uoms;
      },
      error: (error) => {
        // console.error('Error loading units of measure:', error);
      }
    });
  }

  private loadSupplierPriceHistory(): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // console.error('Auth token is missing');
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const url = `${environment.apiUrl}/api/ItemSupplierPriceHistory/GetAll`;
    this.http.get<any[]>(url, { headers }).subscribe({
      next: (PHS) => {
// $&
      },
      error: (error) => {
        // console.error('Error loading units of measure:', error);
      }
    });
  }



  ngOnInit(): void {
    const token = this.authService.getToken && this.authService.getToken();
    if (!token) {
      this.authService.logOut();
      return;
    }
    const decodedToken: any = jwtDecode(token);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();
    this.loadUnitsOfMeasure();
    this.loadItemTypes();
//  this.updateChartData(this.selectedFilter, 1);
    forkJoin({
      categories: this.loadCategories(),
      suppliers: this.loadSuppliers()
    }).subscribe({
      next: (result) => {
        this.categories = result.categories;
        this.suppliers = result.suppliers;
        // Load items after categories and suppliers are loaded
        this.loadItems();
      },
      error: (error) => {
        //console.error('Error loading initial data:', error);
        this.showAlert('Error', 'Failed to load initial data');
        if (error.status === 401) {
          this.authService.logOut();
        }
      }
    });

    // Auto-calculate Unit Cost in Add modal for raw materials as user types
    const purchasePriceCtrl = this.inventoryForm.get('purchasePrice');
    const qtyCtrl = this.inventoryForm.get('quantityToAdd');
    purchasePriceCtrl?.valueChanges.subscribe(() => this.recalculateUnitCost());
    qtyCtrl?.valueChanges.subscribe(() => this.recalculateUnitCost());

  }

  closeLowStockPopup() {
    this.showLowStockPopup = false;
  }




  onFilterChange(newFilter: string) {
    this.selectedFilter = newFilter;
    this.currentFilter = newFilter;
    this.currentPage = 1;
    this.updateChartData(newFilter, 1);
  }
  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.updateChartData(this.selectedFilter, newPage);
  }
  onPageSizeChange = (size: number) => {
    // Update page size from child dropdown, reset to first page, and recompute data
    this.ItemsPerPage = Number(size) > 0 ? Number(size) : 10;
    this.currentPage = 1;

    this.updateChartData(this.selectedFilter, 1);

  }
  sortBy(column: SortableInventoryKeys): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    const compareFn = (a: EnhancedItemDTO, b: EnhancedItemDTO) => {
      const valueA = a[column];
      const valueB = b[column];

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.sortDirection === 'asc'
          ? valueA - valueB
          : valueB - valueA;
      } else {
        return 0;
      }
    };

    this.rawMaterials = [...this.rawMaterials].sort(compareFn);
  }
  sortFinishedBy(column: FinishedSortableKeys) {
    if (this.finishedSortColumn === column) {
      this.finishedSortDirection =
        this.finishedSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.finishedSortColumn = column;
      this.finishedSortDirection = 'asc';
    }

    const sorted = [...this.filteredFinishedGoods].sort((a, b) => {
      const valueA: any = (a as any)[column] ?? '';
      const valueB: any = (b as any)[column] ?? '';

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.finishedSortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.finishedSortDirection === 'asc'
          ? valueA - valueB
          : valueB - valueA;
      } else {
        if (valueA < valueB)
          return this.finishedSortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB)
          return this.finishedSortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });

    this.filteredFinishedGoods = sorted;
  }
  handleRawClick(): void {
    // Reset all states first
    this.resetFormStates();

    // Set specific states for raw material
    this.isFinishedGood = false;
    this.animateClickRaw = true;

    // Reset the form with default values for raw materials
    this.inventoryForm.reset({
      itemName: '',
      description: '',
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      quantityToAdd: 0,
      currentQuantity: 0,
      lowStockThreshold: null, // Set to null to force user input
      valuationMethodCode: 'FIFO',
      itemCode: '',
      PurchaseUOMID: null,
      categoryID: null,
      itemTypeID: 1, // Raw Material
      isActive: true,
      supplierID: null
    });

    // Show the modal
    this.showAddModal = true;

    // Reset animation after delay
    setTimeout(() => {
      this.animateClickRaw = false;
    }, 300);
  }

  compareUOM(uom1: any, uom2: any): boolean {
    // Handle both cases where values could be numbers or UOM objects
    const id1 = typeof uom1 === 'object' ? uom1?.uomid : uom1;
    const id2 = typeof uom2 === 'object' ? uom2?.uomid : uom2;
    return id1 === id2;
  }

  handleFinishedClick(): void {
    // Reset all states first
    this.resetFormStates();

    // Set specific states for finished goods
    this.isFinishedGood = true;
    this.animateClickFinished = true;

    // Reset the form with default values for finished goods
    this.inventoryForm.reset({
      itemName: '',
      description: '',
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      quantityToAdd: 0,
      currentQuantity: 0,
      lowStockThreshold: 0,
      valuationMethodCode: 'FIFO',
      itemCode: '',
      PurchaseUOMID: null,
      categoryID: null,
      itemTypeID: 2, // Finished Good
      isActive: true,
      supplierID: 1 // Default supplier for finished goods
    });

    // Show the modal
    this.showAddModal = true;

    // Reset animation after delay
    setTimeout(() => {
      this.animateClickFinished = false;
    }, 300);
  }
  loadSuppliers() {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<SupplierDTO[]>(
      `${environment.apiUrl}/api/Suppliers/GetAll`,
      { headers }
    ).pipe(
      tap((suppliers) => {
        this.suppliers = suppliers;
      })
    );
  }


  loadCategories(): Observable<CategoryDTO[]> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http
      .get<CategoryDTO[]>(`${environment.apiUrl}/api/Category/GetAll`, {
        headers,
      })
      .pipe(tap((categories) => {
        this.categories = categories;
      }));
  }

  itemTypes: ItemTypes[] = [];

  loadItemTypes(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http.get<ItemTypes[]>(`${environment.apiUrl}/api/ItemTypes/GetAll`, { headers })
      .pipe(
        tap((itemTypes) => {
          this.itemTypes = itemTypes;
        })
      )
      .subscribe();
  }
  get desktopSupplierOptions() {
    return this.suppliers.map(s => ({ value: String(s.supplierID), label: `${s.companyName}${s.primaryEmail ? ' - ' + s.primaryEmail : ''}` }));
  }

  get desktopCategoryOptions() {
    return this.categories.map(c => ({ value: String(c.categoryId), label: c.categoryName }));
  }

  get desktopUomOptions() {
    return this.uomList.map(u => ({ value: String(u.uomid), label: `${u.uomCode}` }));
  }

  onDesktopSupplierSelected(idStr: string | null): void {
    this.selectedSupplier = this.suppliers.find(s => String(s.supplierID) === idStr) ?? null;
  }

  onDesktopCategorySelected(idStr: string | null): void {
    const cat = this.categories.find(c => String(c.categoryId) === idStr) ?? null;
    this.onCategorySelected(cat);
  }

  onDesktopUomSelected(idStr: string | null): void {
    const id = idStr != null && idStr !== '' ? Number(idStr) : null;
    this.inventoryForm.patchValue({ PurchaseUOMID: id });
  }

  onSupplierSelected(supplier: SupplierDTO): void {
    this.selectedSupplier = supplier;
  }

  onCategorySelected(category: CategoryDTO | null): void {
    this.selectedCategory = category;

    if (category) {
      this.inventoryForm.patchValue({
        categoryID: category.categoryId
      });

      // Mark the field as touched and dirty to trigger validation
      this.inventoryForm.get('categoryID')?.markAsTouched();
      this.inventoryForm.get('categoryID')?.markAsDirty();
    } else {
      this.inventoryForm.patchValue({
        categoryID: null
      });
    }
  }
  createSupplier(): void {
    if (this.supplierForm.invalid) {
      Object.keys(this.supplierForm.controls).forEach((field) => {
        const control = this.supplierForm.get(field);
        if (control) {
          control.markAsTouched({ onlySelf: true });
        }
      });
      return;
    }

    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const newSupplier: SupplierDTO = {
      supplierID: 0,
      companyName: this.supplierForm.value.companyName,
      primaryContactPerson: this.supplierForm.value.primaryContactPerson,
      primaryEmail: this.supplierForm.value.primaryEmail,
      primaryPhone: this.supplierForm.value.primaryPhone,
      website: this.supplierForm.value.website,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.http
      .post<SupplierDTO>(
        `${environment.apiUrl}/api/Suppliers/Add`,
        newSupplier,
        { headers }
      )
      .pipe(
        // After supplier is created, create the location
        switchMap((supplierResponse) => {
          const newLocation = {
            locationID: 0,
            supplierID: supplierResponse.supplierID,
            branchName: this.supplierForm.value.branchName,
            addressLine1: this.supplierForm.value.addressLine1,
            addressLine2: this.supplierForm.value.addressLine2,
            city: this.supplierForm.value.city,
            region: this.supplierForm.value.region,
            postalCode: this.supplierForm.value.postalCode,
            country: this.supplierForm.value.country,
            locationContactPerson: this.supplierForm.value.locationContactPerson,
            locationEmail: this.supplierForm.value.locationEmail,
            locationPhone: this.supplierForm.value.locationPhone,
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          return this.http.post<SupplierLocationDTO>(
            `${environment.apiUrl}/api/SupplierLocations/Add`,
            newLocation,
            { headers }
          ).pipe(
            map(locationResponse => ({
              supplier: supplierResponse,
              location: locationResponse
            }))
          );
        })
      )
      .subscribe({
        next: (response) => {
          // Combine supplier and location data
          const supplierWithLocation = {
            ...response.supplier,
            locations: [response.location]
          };

          this.selectedSupplier = supplierWithLocation;
          this.isCreatingSupplier = false;
          this.supplierForm.reset();
          this.suppliers.push(supplierWithLocation);

          this.toast.success('Supplier and location created successfully', 3000);
        },
        error: (err) => {
          //console.error('Error creating supplier or location:', err);
          this.toast.error('Failed to create supplier or location. Please try again.');
        },
      });
  }


  createCategory(): void {
    if (this.categoryForm.invalid) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      //console.error('Auth token is missing');
      return;
    }
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const newCategory: CategoryDTO = {
      categoryId: 0,
      categoryName: this.categoryForm.value.categoryName,
      createdAt: new Date(),
    };
    this.http
      .post<CategoryDTO>(
        `${environment.apiUrl}/api/Category/Add`,
        newCategory,
        { headers }
      )
      .subscribe({
        next: (response) => {
          this.selectedCategory = response;
          this.onCategorySelected(response);
          this.isCreatingCategory = false;
          this.categoryForm.reset();
          this.categories.push(response);
        },
        error: (err) => console.error('Error creating category:', err),
      });
  }
  // Method to deactivate an item instead of deleting it
  deleteItem(item: EnhancedItemDTO): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      //console.error('Auth token is missing. Please log in again.');

      this.toast.error('Session expired. Please log in again.', 3000);
      setTimeout(() => { this.authService.logOut(); }, 3000);
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });

    // Create a copy of the item with isActive set to false
    const updatedItem = { ...item, isActive: false };

    this.http
      .put(
        `${environment.apiUrl}/api/Items/Update?ItemId=${item.itemID}`,
        updatedItem,
        { headers }
      )
      .pipe(
        catchError(error => {
          //console.error('Error deactivating item:', error);

          let msg = 'Failed to remove item. Please try again.';
          if (error.status === 404) msg = 'Item not found.';
          else if (error.status === 401) msg = 'Unauthorized. Please log in again.';
          else if (error.status === 409) msg = 'Cannot remove item as it is referenced by other records.';
          this.toast.error(msg, 3000);

          throw error;
        })
      )
      .subscribe({
        next: () => {
          const rawMaterialTypeId = this.itemTypes.find(type => type.typeName === 'Raw Material')?.itemTypeID;
          const finishedGoodTypeId = this.itemTypes.find(type => type.typeName === 'Finished Good')?.itemTypeID;
          const variableGoodTypeIds = this.itemTypes
            .filter(type => type.typeName !== 'Raw Material' && type.typeName !== 'Finished Good')
            .map(type => type.itemTypeID);
          // Update UI lists
          if (item.itemTypeID === rawMaterialTypeId) {
            this.rawMaterials = this.rawMaterials.filter(
              (rm) => rm.itemID !== item.itemID
            );
            this.filteredRawMaterials = this.filteredRawMaterials.filter(
              (rm) => rm.itemID !== item.itemID
            );
          } else if (item.itemTypeID === finishedGoodTypeId) {
            this.finishedGoods = this.finishedGoods.filter(
              (fg) => fg.itemID !== item.itemID
            );
            this.filteredFinishedGoods = this.filteredFinishedGoods.filter(
              (fg) => fg.itemID !== item.itemID
            );
          }
          else if (variableGoodTypeIds.includes(item.itemTypeID)) {
            this.variableItemType = this.variableItemType.filter(
              (vi) => vi.itemID !== item.itemID
            );
            // If you have a filtered version of variableItemType, update it here as well
          }


          // Update statistics and charts
          this.calculateStatistics();
          this.prepareChartsData();
          this.loadrawmattotal();
          // Reset UI state
          this.showAddModal = false;
          this.selectedItem = null;
          this.selectedSupplier = null;
          this.selectedCategory = null;
          this.inventoryForm.reset();

          this.showAlert('Success', `Item ${item.itemName} deactivated successfully`, 'bg-success');
        }
      });

      this.loadItems();
      setTimeout(() => {
        this.loadrawmattotal();
        this.calculateStatistics();

      }, 500);


  }
  generateSKU(productName: string): string {
    if (!productName) return '';

    // Normalize: trim, remove diacritics, keep alphanumerics and spaces, uppercase
    const cleanName = productName
      .trim()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // strip diacritics
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toUpperCase();

    const words = cleanName.split(/\s+/).filter(Boolean);

    let letters: string;
    if (words.length >= 2) {
      letters = (words[0][0] || 'X') + (words[1].substring(0, 2) || 'XX');
      letters = letters.padEnd(3, 'X');
    } else if (words.length === 1) {
      letters = words[0].substring(0, 3).padEnd(3, 'X');
    } else {
      letters = 'XXX';
    }

    // Deterministic hash (DJB2 variant) for stability
    const hash = (str: string): number => {
      let h = 5381;
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h) ^ str.charCodeAt(i); // h * 33 ^ char
      }
      return Math.abs(h);
    };

    // Middle segment from alphabetic-only name => stable across numeric variants
    const alphaOnly = cleanName.replace(/[^A-Z]/g, '');
    const mid = hash(alphaOnly || cleanName) % 1000; // 3 digits

    // Last segment from full name (incl. numbers) => deterministic uniqueness per variant
    const last = hash(cleanName + '|SKU') % 1000; // 3 digits

    return `${letters}-${mid.toString().padStart(3, '0')}-${last.toString().padStart(3, '0')}`;
  }
  handleResponse(response: ItemDTO): void {
    const enhancedItem: EnhancedItemDTO = {
      ...response,
      CategoryName: this.selectedCategory?.categoryName || 'Unknown',
    };

    if (this.isEditing) {
      const targetArray =
        response.itemTypeID === 1 ? this.rawMaterials : this.finishedGoods;
      const index = targetArray.findIndex((i) => i.itemID === response.itemID);
      if (index > -1) targetArray[index] = enhancedItem;
    } else {
      if (response.itemTypeID === 1) {
        this.rawMaterials.push(enhancedItem);
      } else if (response.itemTypeID === 2) {
        this.finishedGoods.push(enhancedItem);
      } else {
        this.variableItemType.push(enhancedItem);
      }
    }
    this.calculateStatistics();
    this.prepareChartsData();
    this.selectedSupplier = null;
    this.selectedCategory = null;
    this.showAddModal = false;
    this.selectedItem = null;
  }

  loadItems(): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      //console.error('Auth token is missing');
      return;
    }
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    forkJoin([
      this.http.get<EnhancedItemDTO[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers }),
      this.http.get<InventoryTransactionDTO[]>(`${environment.apiUrl}/api/InventoryTransactions/GetAll`, { headers })
    ]).subscribe({
      next: async ([items, transactions]) => {
        // Create category name map
        const categoryMap = new Map<number, string>();
        this.categories.forEach(category => {
          categoryMap.set(category.categoryId, category.categoryName);
        });

        // Group transactions by item ID for better performance
        const transactionsByItem = new Map<number, InventoryTransactionDTO[]>();
        transactions.forEach(tx => {
          if (!transactionsByItem.has(tx.itemID)) {
            transactionsByItem.set(tx.itemID, []);
          }
          transactionsByItem.get(tx.itemID)?.push(tx);
        });

        // Calculate quantities, average cost and set category names for each item
        const itemsWithQuantities = items.map(item => {
          const itemTransactions = transactionsByItem.get(item.itemID) || [];
          const { totalQuantity, totalCost } = this.calculateItemMetrics(itemTransactions);

          // Get the latest unit cost from the most recent inventory transaction
          const latestTransaction = itemTransactions
            .filter(tx => tx.costPerUnit !== null && tx.costPerUnit !== undefined)
            .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];

          const latestUnitCost = latestTransaction ? latestTransaction.costPerUnit : (item.unitCost || item.purchasePrice || 0);

          return {
            ...item,
            quantity: this.calculateItemQuantity(item.itemID, transactions),
            averageCost: totalQuantity > 0 ? totalCost / totalQuantity : 0,
            unitCost: latestUnitCost, // Override with latest transaction unit cost
            CategoryName: categoryMap.get(item.categoryID) || 'Unknown'
          };
        });

        // Log all items before filtering
        // Separate into raw materials and finished goods, filtering out inactive items
        const rawMaterials = itemsWithQuantities.filter(item => item.itemTypeID === 1 && item.isActive !== false);
        const finishedGoods = itemsWithQuantities.filter(item => item.itemTypeID === 2 && item.isActive !== false);
        const rawMaterialsForTotal =itemsWithQuantities.filter(item => item.itemTypeID === 1 && item.isActive !== false);
        this.rawMaterials = rawMaterials;
        this.originalRawMaterials = [...rawMaterials];  // Keep a copy of the original
        this.finishedGoods = finishedGoods;
        this.originalFinishedGoods = [...finishedGoods];  // Keep a copy of the original

        // Initialize filtered arrays
        this.filteredRawMaterials = [...rawMaterials];
        this.filteredFinishedGoods = [...finishedGoods];

        // Update filtered items for search
        this.filteredItems = [...this.rawMaterials, ...this.finishedGoods];

        this.loadrawmattotal();
        const [bomHeaders, allBomComponents] = await Promise.all([
          this.http.get<BOMHeaders[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, { headers }).toPromise(),
          this.http.get<BOMComponents[]>(`${environment.apiUrl}/api/BOMComponents/GetAll`, { headers }).toPromise()
        ]);


        for (const product of finishedGoods) {
          // Find active BOM for this product
          const productBOM = (bomHeaders || []).find(bom =>
            bom.finishedGoodItemID === product.itemID
          );
          if (!productBOM) {
            //console.error(`No active BOM found for product ID: ${product.itemID}`);
            continue;
          }

          // Get components for this BOM
          const bomComponents = (allBomComponents || []).filter(comp =>
            comp.bomHeaderID === productBOM.bomHeaderID
          );
          if (bomComponents.length === 0) continue;
          bomComponents.forEach(comp => {
            const item = this.filteredItems.find(i => i.itemID === comp.rawMaterialItemID);
          });
          if (product.isActive) {  // Only process active products
            // Calculate total cost for this product's BOM
            const bomCost = bomComponents.reduce((bomTotal, comp) => {
              const componentItem = this.filteredItems.find(item => item.itemID === comp.rawMaterialItemID);
              if (!componentItem) return bomTotal;

              const componentUnitCost = (componentItem.purchasePrice / componentItem.purchaseQuantity )|| 0;
              return bomTotal + (comp.quantityRequired * componentUnitCost);
            }, 0);
            this.totalProductValueFromBOM += product.quantity * bomCost;
          }
        }


        // Update statistics and charts
        this.calculateStatistics();
        this.prepareChartsData();
        this.updateChartData(this.selectedFilter, 1);
        this.isLoading = false;
      },
      error: (error) => {
        //console.error('Error loading items:', error);
        this.showAlert('Error', 'Failed to load inventory data');
      }
    });
  }


loadrawmattotal(){
  let totalRawMaterialValuePre = this.rawMaterials;
  totalRawMaterialValuePre = this.rawMaterials.filter(item => item.itemTypeID === 1 && item.isActive !== false);
  this.totalRawMaterialValue = totalRawMaterialValuePre.reduce((total, item) => {
    return total + (item.quantity || 0) * (item.unitCost || 0);
  }, 0);
}
  // Add this new method to calculate item metrics
  private calculateItemMetrics(transactions: InventoryTransactionDTO[]): { totalQuantity: number; totalCost: number } {
    return transactions.reduce((acc, tx) => {
      if (tx.transactionType === 'Receipt') {
        acc.totalQuantity += tx.quantityChange;
        acc.totalCost += tx.quantityChange * (tx.costPerUnit || 0);
      }
      return acc;
    }, { totalQuantity: 0, totalCost: 0 });
  }
  prepareChartsData(filter: 'all' | 'raw' | 'finished' = 'all'): void {
    const categoryMap = new Map<string, number>();
    const categoryQuantityMap = new Map<string, number>();

    let filteredItems: EnhancedItemDTO[] = [];

    if (filter === 'raw') {
      filteredItems = this.rawMaterials.filter(item => item.isActive);
    } else if (filter === 'finished') {
      filteredItems = this.finishedGoods.filter(item => item.isActive);
    } else {
      filteredItems = [...this.rawMaterials, ...this.finishedGoods].filter(item => item.isActive);
    }
    // If no items, reset the chart data and return
    if (filteredItems.length === 0) {
      this.categoryLabels = [];
      this.categoryData = [];
      this.stackedBarData = null;
      return;
    }

    // Calculate total stock value and quantity per category
    filteredItems.forEach((item) => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      // Update category maps
      const currentValue = categoryMap.get(categoryName) || 0;
      const currentQuantity = categoryQuantityMap.get(categoryName) || 0;

      categoryMap.set(categoryName, currentValue + itemValue);
      categoryQuantityMap.set(categoryName, currentQuantity + itemQuantity);
    });
    // Sort categories by total value (descending)
    const sortedCategoryEntries = Array.from(categoryMap.entries())
      .filter(([_, value]) => value > 0)
      .sort((a, b) => b[1] - a[1]);

    const categoryLabels = sortedCategoryEntries.map(([key]) => key);
    const categoryValues = sortedCategoryEntries.map(([_, value]) => value);
    // For pie chart (stock quantity by category)
    const sortedQuantityEntries = Array.from(categoryQuantityMap.entries())
      .filter(([_, value]) => value > 0)
      .sort((a, b) => b[1] - a[1]);

    // Update component properties for pie chart
    this.categoryLabels = sortedQuantityEntries.map(([key]) => key);
    this.categoryData = sortedQuantityEntries.map(([_, value]) => value);

    // For bar chart (stock value by category)
    this.stackedBarData = {
      labels: categoryLabels,
      datasets: [
        {
          label: 'Stock Value',
          data: categoryValues,
          backgroundColor: this.baseColors.slice(0, categoryLabels.length),
          borderRadius: 4,
          borderWidth: 0,
        }
      ]
    };

    // Log the final state
  }
  updateChartData(type: string, page: number = 1) {
    // Build category-level totals (stock value) similar to prepareChartsData()
    let filteredItems: EnhancedItemDTO[] = [];
    if (type === 'raw') {
      this.selectedDatasetLabel = 'Raw Materials';
      filteredItems = this.rawMaterials;
    } else if (type === 'finished') {
      this.selectedDatasetLabel = 'Finished Goods';
      filteredItems = this.finishedGoods;
    } else {
      this.selectedDatasetLabel = 'All';
      filteredItems = [...this.rawMaterials, ...this.finishedGoods];
    }

    if (!filteredItems || filteredItems.length === 0) {
      this.totalItemsCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.stackedBarData = {
        labels: [],
        datasets: [
          {
            label: 'Stock Value',
            data: [],
            backgroundColor: [],
            borderRadius: 4,
            borderWidth: 0,
          },
        ],
      };
      return;
    }

    const categoryMap = new Map<string, number>();
    filteredItems.forEach((item) => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      const current = categoryMap.get(categoryName) || 0;
      categoryMap.set(categoryName, current + itemValue);
    });

    // Sort by value desc and remove zero-value categories
    const sortedEntries = Array.from(categoryMap.entries())
      .filter(([_, value]) => value > 0)
      .sort((a, b) => b[1] - a[1]);

    const allLabels = sortedEntries.map(([key]) => key);
    const allValues = sortedEntries.map(([_, value]) => value);

    // Total items for pagination header in child component
    this.totalItemsCount = allLabels.length;

    // Pagination over categories: compute pages only; actual slicing is handled by BarChartComponent
    const itemsPerPage = Math.max(1, this.ItemsPerPage || 10);
    this.totalPages = Math.max(1, Math.ceil(allLabels.length / itemsPerPage));
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));

    this.stackedBarData = {
      labels: allLabels,
      datasets: [
        {
          label: 'Stock Value',
          data: allValues,
          backgroundColor: this.baseColors.slice(0, allLabels.length),
          borderRadius: 4,
          borderWidth: 0,
        },
      ],
    };
  }
  calculateStatistics(): void {
    const allItems = [...this.rawMaterials, ...this.finishedGoods];
    this.lowStockItemsList = [...this.rawMaterials]
      .filter(item => {
        // Check if item has a low stock threshold and current quantity is at or below it
        return item.lowStockThreshold !== null &&
          item.lowStockThreshold !== undefined &&
          (item.quantity || 0) <= item.lowStockThreshold;
      })
      .map(item => ({
        name: item.itemName,
        quantity: item.quantity || 0,
        threshold: item.lowStockThreshold || 0
      }));
    this.totalRawMaterials = this.rawMaterials.length;
    this.totalFinishedGoods = this.finishedGoods.length;
    this.mobileNavService.updateBadges({ raw: this.rawMaterials.length, products: this.finishedGoods.length });
    this.lowStockItems = this.rawMaterials.filter(
      (item) =>
        item.itemTypeID === 1 &&
        (item.quantity ?? 0) < (item.lowStockThreshold ?? 0)
    ).length;
    // this.totalRawMaterialValue = this.rawMaterials.reduce(
    //   (sum, item) => sum + (item.quantity ?? 0) * (item.unitCost ?? 0),
    //   0
    // );

    // Show low stock popup if there are low stock items and it hasn't been shown in this session
    if (this.lowStockItems > 0 && !sessionStorage.getItem('showLowStockPopup')) {
      this.showLowStockPopup = true;
      sessionStorage.setItem('showLowStockPopup', 'true');
    }

    // Check for items with reorder level = 0 (likely added from cost calculator)
    this.zeroReorderItems = this.rawMaterials.filter(
      (item) => (item.lowStockThreshold == null || item.lowStockThreshold <= 0) && item.isActive !== false
    );
    if (this.zeroReorderItems.length > 0) {
      this.reorderEditValues = {};
      this.zeroReorderItems.forEach((item) => {
        this.reorderEditValues[item.itemID] = 0;
      });
      this.showReorderPrompt = true;
    }
  }

  closeReorderPrompt(): void {
    this.showReorderPrompt = false;
  }

  async saveReorderLevels(): Promise<void> {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    for (const item of this.zeroReorderItems) {
      const newThreshold = this.reorderEditValues[item.itemID];
      if (newThreshold != null && newThreshold > 0) {
        const updateDto = {
          ...item,
          lowStockThreshold: newThreshold,
        };
        try {
          await this.http.put(
            `${environment.apiUrl}/api/Items/Update?ItemId=${item.itemID}`,
            updateDto,
            { headers }
          ).toPromise();
          item.lowStockThreshold = newThreshold;
        } catch (err) {
          console.error(`Failed to update reorder level for ${item.itemName}:`, err);
        }
      }
    }

    this.showReorderPrompt = false;

    // Recalculate so the button disappears
    this.zeroReorderItems = this.rawMaterials.filter(
      (item) => (item.lowStockThreshold == null || item.lowStockThreshold <= 0) && item.isActive !== false
    );

    this.showAlert('Success', 'Reorder levels updated successfully', '#4de270', '#ffffff');
  }
  calculateItemQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const itemTransactions = transactions.filter(transaction => transaction.itemID === itemID);
    const quantity = itemTransactions.reduce((sum, transaction) => sum + transaction.quantityChange, 0);
    return quantity;
  }

  // Compute unit cost = purchasePrice / initial quantity (only for Add modal of raw materials)
  private recalculateUnitCost(): void {
    // Only auto-calc when adding a new raw material (not editing, not finished good, not add-quantity only)
    if (this.isFinishedGood || this.isEditing || this.isAddingQuantity) {
      return;
    }

    const purchasePrice = Number(this.inventoryForm.get('purchasePrice')?.value) || 0;
    const qty = Number(this.inventoryForm.get('quantityToAdd')?.value) || 0;

    const unitCost = qty > 0 ? parseFloat((purchasePrice / qty).toFixed(2)) : 0;
    // Patch without emitting to avoid loops
    this.inventoryForm.get('unitCost')?.setValue(unitCost, { emitEvent: false });
  }
  showAlert(title: string, message: string, bgColor: string = 'bg-danger', textColor: string = 'text-white'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertBgColor = bgColor;
    this.alerttextcolor = textColor;
    this.isAlertVisible = true;
    setTimeout(() => { this.isAlertVisible = false; }, 5000);
  }

  openAddOption(): void {
    // Reset all form states before showing the add option modal
    this.resetFormStates();
    this.showAddOptionModal = true;
  }

  selectAddOption(option: 'new' | 'existing'): void {
    if (option === 'new') {
      // Reset all states and show the original add/edit modal
      this.isAddingQuantity = false;
      this.selectedItem = null;
      this.showAddOptionModal = false;
      this.showAddModal = true;
    } else {
      // Show item selection modal for adding quantity
      this.showItemSelectModal = true;
    }
  }

  showItemSelectionDialog(): void {
    this.showItemSelectModal = true;
  }

  closeItemSelectModal(): void {
    this.showItemSelectModal = false;
  }

  selectItemForQuantityAdd(item: EnhancedItemDTO): void {
    // Close the add option modal first
    this.showAddOptionModal = false;

    this.selectedItem = item;
    this.isAddingQuantity = true;
    this.isEditing = false;
    this.showAddModal = true;

    // Reset the form and set the correct values
    this.inventoryForm.reset();
    this.inventoryForm.patchValue({
      itemName: item.itemName,
      quantityToAdd: 1,
      currentQuantity: item.quantity || 0,
      unitCost: item.purchasePrice || 0,
      purchasePrice: item.purchasePrice || 0
    });

    this.closeItemSelectModal();
  }

  addQuantityToItem(): void {
    const formValue = this.inventoryForm.getRawValue();
    if (!this.selectedItem || !formValue.quantityToAdd || formValue.quantityToAdd <= 0) {
      this.showAlert('Validation Error', 'Invalid quantity');
      return;
    }

    if (!formValue.unitCost || formValue.unitCost <= 0) {
      this.showAlert('Validation Error', 'Please enter a valid unit cost');
      return;
    }

    const currentDate = new Date();
    const transactionData = {
      ItemID: this.selectedItem.itemID,
      WarehouseID: 1, // Default warehouse ID, adjust as needed
      TransactionType: 'Receipt',
      QuantityChange: Number(formValue.quantityToAdd),
      CostPerUnit: this.selectedItem.purchasePrice / this.selectedItem.purchaseQuantity,
      TransactionDate: currentDate.toISOString(),
      ReferenceType: 'Manual',
      ReferenceID: `ADD-${currentDate.getTime()}`,
      UserID: 1, // Get from auth service
      Notes: 'Quantity added via inventory dashboard',
      BatchNumber: null,
      ExpiryDate: null,
      ReferenceTypeCode: 'MANUAL'
    };
    const token = localStorage.getItem('authToken');
    if (!token) {
      //console.error('Auth token is missing');
      return;
    }
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, transactionData, { headers })
      .subscribe({
        next: (response) => {
          this.showAlert('Success', 'Quantity added successfully', 'bg-success');
          this.loadItems();
          this.closeAddModal();
        },
        error: (error) => {
          //console.error('Error adding quantity:', error);
          this.showAlert('Error', 'Failed to add quantity. Please try again.', 'bg-danger');
        }
      });
  }

  saveItem(): void {
    if (this.isAddingQuantity) {
      this.addQuantityToItem();
      return;
    }

    const formValue = this.inventoryForm.getRawValue();

    // If editing a raw material, check if the price has changed
    if (this.isEditing && this.selectedItem && !this.isFinishedGood) {
      const oldPrice = this.selectedItem.purchasePrice;
      const newPrice = formValue.purchasePrice;
      if (oldPrice !== newPrice) {
        this.addItemPriceHistory(this.selectedItem.itemID, newPrice, formValue.quantityToAdd || 1);
      }
    }

    // Validate required fields
    if (!formValue.categoryID) {
      this.showAlert('Validation Error', 'Please select a category');
      return;
    }

    if (!formValue.PurchaseUOMID) {
      this.showAlert('Validation Error', 'Please select a unit of measure');
      return;
    }

    // Require reorder level for raw materials
    if (!this.isFinishedGood && (formValue.lowStockThreshold === null || formValue.lowStockThreshold === undefined || formValue.lowStockThreshold === '')) {
      this.showAlert('Validation Error', 'Please enter a reorder level for raw materials');
      return;
    }

    // Validate supplier is selected for raw materials
    if (!this.isFinishedGood && !this.selectedSupplier) {
      this.showAlert('Validation Error', 'Please select a supplier for raw materials');
      return;
    }

    // Ensure we have a valid ItemTypeID
    let itemTypeId = 1; // Default to Raw Material (1) or Finished Good (2)
    if (formValue.itemType && formValue.itemType.itemTypeID) {
      itemTypeId = formValue.itemType.itemTypeID;
    } else if (this.isFinishedGood) {
      itemTypeId = 2; // Default to Finished Good if isFinishedGood is true
    }


    const itemData: any = {
      ItemID: this.isEditing && this.selectedItem ? this.selectedItem.itemID : 0,
      ItemName: formValue.itemName,
      ItemCode: formValue.itemCode || this.generateSKU(formValue.itemName),

      itemTypeID: itemTypeId,
      PurchaseUOMID: formValue.PurchaseUOMID,
      lowStockThreshold: formValue.lowStockThreshold ?? 0,
      isActive: true,
      valuationMethodCode: formValue.valuationMethodCode || 'FIFO',
      categoryID: formValue.categoryID,
      profitMarginPercent: formValue.profitMarginPercent || 0,
      sellingPrice: formValue.sellingPrice || 0,
      supplierID: this.isFinishedGood ? 1 : (this.selectedSupplier ? this.selectedSupplier.supplierID : null)
    };

    // Handle purchase data based on editing state and changes
    let hasActualChanges = false;

    if (!this.isEditing) {
      // For new items, always include purchase data
      itemData.purchasePrice = formValue.purchasePrice || 0;
      itemData.unitCost = formValue.unitCost || 0;
      itemData.purchaseQuantity = formValue.quantityToAdd || 1;
      itemData.quantity = formValue.quantityToAdd || 0;
      hasActualChanges = true;
    } else if (this.isEditing && this.showPurchaseFields && this.selectedItem) {
      // For editing, check if values have actually changed
      const originalPrice = this.selectedItem.purchasePrice || 0;
      const originalQuantity = this.selectedItem.quantity || 0;
      const newPrice = formValue.purchasePrice || 0;
      const newQuantity = formValue.quantityToAdd || 0;

      const priceChanged = Math.abs(newPrice - originalPrice) > 0.01;
      const quantityChanged = newQuantity !== originalQuantity && newQuantity > 0;

      if (priceChanged || quantityChanged) {
        // Only include changed values for inventory transaction
        itemData.purchasePrice = newPrice;
        itemData.unitCost = formValue.unitCost || 0;
        itemData.purchaseQuantity = newQuantity;
        itemData.quantity = newQuantity;
        hasActualChanges = true;
      } else {
        // NO CHANGES - Preserve existing values
        hasActualChanges = false;
        itemData.purchasePrice = this.selectedItem.purchasePrice || 0;
        itemData.unitCost = this.selectedItem.unitCost || 0;
        itemData.purchaseQuantity = this.selectedItem.purchaseQuantity || 1;
        itemData.quantity = this.selectedItem.quantity || 0;
      }
    } else if (this.isEditing && !this.showPurchaseFields && this.selectedItem) {
      // When purchase fields are hidden, preserve existing values
      itemData.purchasePrice = this.selectedItem.purchasePrice || 0;
      itemData.unitCost = this.selectedItem.unitCost || 0;
      itemData.purchaseQuantity = this.selectedItem.purchaseQuantity || 1;
      itemData.quantity = this.selectedItem.quantity || 0;
    }

    // Store the flag for inventory transaction logic
    (itemData as any).hasActualChanges = hasActualChanges;
    // Set cost and price based on item type
    if (this.isFinishedGood) {
      itemData.sellingPrice = formValue.sellingPrice || 0;
      itemData.purchasePrice = 0;
    } else {

      // For raw materials, require supplier
      if (!this.selectedSupplier) {
        throw new Error('Supplier is required for raw materials');
      }
      itemData.supplierID = this.selectedSupplier.supplierID;
    }
    const apiUrl = this.isEditing && this.selectedItem
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${this.selectedItem.itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const httpMethod = this.isEditing ? 'put' : 'post';
    // Ensure token is not null
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    // Store the subscription to properly handle it
    const subscription = this.http.request<ItemDTO>(httpMethod, apiUrl, {
      body: itemData,
      headers,
      observe: 'response',
      responseType: 'json'
    }).pipe(
      tap(response => {
      }),
      switchMap((response: any) => {
        if (response.status >= 400) {
          //console.error('Server returned error status:', response.status);
          if (response.body) {
            //console.error('Error details:', response.body);
          }
          throw new Error(`Server returned status ${response.status}`);
        }

        const itemResponse = response.body;
        if (!itemResponse) {
          throw new Error('No response body received from server');
        }

        const savedItemId = this.isEditing && this.selectedItem ? this.selectedItem.itemID : itemResponse.itemID;

        // Only create inventory transaction if there are actual changes or it's a new item
        if ((itemData as any).hasActualChanges) {
          const inventoryTransaction: InventoryTransactionDTO = {
            itemID: savedItemId,
            warehouseID: 1, // Default warehouse ID, adjust as needed
            transactionType: this.isEditing ? 'Adjustment' : 'Receipt',
            quantityChange: formValue.quantityToAdd || 0,  // Changed from formValue.purchaseQuantity to formValue.quantityToAdd
            costPerUnit: formValue.unitCost || 0, // Use manually entered unit cost
            lowStockThreshold: formValue.lowStockThreshold !== undefined ? formValue.lowStockThreshold : 0,
            transactionDate: new Date(),
            referenceType: 'Manual Entry',
            referenceID: `UI-${new Date().getTime()}`,
            userID: 1,
            notes: `${this.isEditing ? 'Updated' : 'Added'} via Inventory Dashboard`,
            batchNumber: null,
            expiryDate: null
          };
          const token = localStorage.getItem('authToken');

          const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
          return this.http.post(
            `${environment.apiUrl}/api/InventoryTransactions/Add`,
            inventoryTransaction,
            { headers }
          ).pipe(
            map(() => ({ itemId: savedItemId, isFinishedGood: this.isFinishedGood }))
          );
        } else {
          // No inventory transaction needed, just return success
          return of({ itemId: savedItemId, isFinishedGood: this.isFinishedGood });
        }
      })
    ).subscribe({
      next: (response) => {
        this.loadItems();
        this.showAddModal = false;
        this.inventoryForm.reset();
        this.showAlert('Success', `Item ${this.isEditing ? 'updated' : 'added'} successfully`, '#4de270');
      },
      error: (error) => {
        //console.error('Error in save operation:', {
        //   status: error.status,
        //   statusText: error.statusText,
        //   error: error.error,
        //   url: error.url,
        //   headers: error.headers,
        //   fullError: error
        // });

        // Log detailed error information
        // console.error('Error details:', {
        //   status: error.status,
        //   statusText: error.statusText,
        //   url: error.url,
        //   error: error.error,
        //   headers: error.headers
        // });

        if (error.error) {
          try {
            const errorObj = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
            //console.error('Server error details:', errorObj);
            this.errorMessage = errorObj.title || 'Failed to save item. Please try again.';
            if (errorObj.errors) {
              const errorMessages = Object.entries(errorObj.errors)
                .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('\n');
              this.errorMessage = errorMessages || this.errorMessage;
            }
          } catch (e) {
            //console.error('Error parsing error response:', e);
            this.errorMessage = 'Failed to save item. Please try again.';
          }
        } else {
          this.errorMessage = 'Failed to save item. Please try again.';
        }

        this.showAlert('Error', this.errorMessage || 'Failed to save item. Please try again.', 'bg-danger');
        if (error.error) {
          try {
            const errorObj = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
            //console.error('Server error details:', errorObj);
            if (errorObj.errors) {
              //console.error('Validation errors:', errorObj.errors);
            }
          } catch (e) {
            //console.error('Error parsing error response:', e);
          }
        }

      }
    });
  }


  compareCategory(cat1: any, cat2: any): boolean {
    return cat1 && cat2 ? cat1.categoryId === cat2.categoryId : cat1 === cat2;
  }

  // Get the name of a unit of measure by its ID
  getUOMName(uomId: number): string {
    if (!uomId) return '';
    const uom = this.uomList.find(u => u.uomid === uomId);
    return uom ? uom.uomCode.replace(/2$/, '²') : '';
  }

  getSelectedUomCode(): string {
    const id = this.inventoryForm.get('PurchaseUOMID')?.value;
    if (!id) return '';
    return this.uomList.find(u => u.uomid === id)?.uomCode ?? '';
  }

  setQuantityToAdd(val: number): void {
    this.inventoryForm.get('quantityToAdd')?.setValue(val);
  }

  // Import/Export properties
  isImporting = false;
  importProgress = 0;
  importResults: { success: number; errors: any[]; warnings: any[] } = { success: 0, errors: [], warnings: [] };

  get errorCount(): number {
    return this.importResults?.errors?.length || 0;
  }

  get warningCount(): number {
    return this.importResults?.warnings?.length || 0;
  }

  get failedRows(): any[] {
    return this.importResults?.errors || [];
  }

  get purchasePriceError(): string | null {
    const control = this.inventoryForm?.get('purchasePrice');
    return control?.errors?.['invalidPrice'] || null;
  }
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('importProgressModal') importProgressModal: any;



  // Cancel the supplier form and reset it
  cancelSupplier(): void {
    this.supplierForm.reset();
    this.isCreatingSupplier = false;
  }

  // Import/Export methods
  async downloadTemplate(): Promise<void> {
    try {
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);
      const suppliers = this.suppliers.map(s => s.companyName);

      await this.excelService.generateInventoryTemplate(categories, uoms, suppliers);
    } catch (error) {
// $&
      this.showAlert('Error', 'Failed to generate template. Please try again.', 'bg-danger');
    }
  }

  async downloadFinishedGoodTemplate(): Promise<void> {
    try {
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);

      await this.excelService.generateProductsTemplate(categories, uoms);
    } catch (error) {
// $&
      this.showAlert('Error', 'Failed to generate template. Please try again.', 'bg-danger');
    }
  }

  // Determine if an error row is a finished good based on available columns
  private isFinishedGoodErrorRow(row: any): boolean {
    if (!row) return false;
    const val = (k: string) => row[k] !== undefined && row[k] !== null && row[k] !== '';
    // Finished goods template produces 'sellingprice'; also respect optional type flags
    return val('sellingprice')
      || String(row?.itemtype || '').toLowerCase() === 'finished'
      || String(row?.itemtypeid || '').toLowerCase() === '2';
  }

  hasFinishedGoodErrors(): boolean {
    if (!this.importResults?.errors?.length) return false;
    return this.importResults.errors.some(e => this.isFinishedGoodErrorRow(e.item));
  }

  async downloadFailedFinishedGoods(): Promise<void> {
    try {
      const failedFG = (this.importResults?.errors || [])
        .map(e => e.item)
        .filter(row => this.isFinishedGoodErrorRow(row));
      if (!failedFG.length) {
        this.showAlert('No Finished Goods', 'There are no failed finished goods rows to export.', 'bg-warning');
        return;
      }

      const dataRows = failedFG.map(row => ({
        itemName: String(row['itemname*'] || row['itemname'] || '').trim(),
        itemCode: String(row['sku'] || row['itemcode'] || '').trim(),
        category: String(row['category'] || '').trim(),
        uom: String(row['unitofmeasure'] || row['uom'] || '').trim(),
        sellingPrice: row['sellingprice'] ?? null
      }));
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);
      await this.excelService.generateProductsTemplate(categories, uoms, dataRows);
    } catch (error) {
// $&
      this.showAlert('Error', 'Failed to export failed rows. Please try again.', 'bg-danger');
    }
  }

  // Determine raw material rows: treat any non-finished-good error row as raw material
  private isRawMaterialErrorRow(row: any): boolean {
    if (!row) return false;
    return !this.isFinishedGoodErrorRow(row);
  }

  hasRawMaterialErrors(): boolean {
    if (!this.importResults?.errors?.length) return false;
    return this.importResults.errors.some(e => this.isRawMaterialErrorRow(e.item));
  }

  async downloadFailedRawMaterials(): Promise<void> {
    try {
      const failedRM = (this.importResults?.errors || [])
        .map(e => e.item)
        .filter(row => this.isRawMaterialErrorRow(row) && !this.isFinishedGoodErrorRow(row));
      if (!failedRM.length) {
        this.showAlert('No Raw Materials', 'There are no failed raw material rows to export.', 'bg-warning');
        return;
      }

      const dataRows = failedRM.map(row => ({
        itemName: String(row['itemname*'] || row['itemname'] || '').trim(),
        itemCode: String(row['sku'] || row['itemcode'] || '').trim(),
        description: String(row['description'] || '').trim(),
        category: String(row['category'] || '').trim(),
        uom: String(row['unitofmeasure'] || row['uom'] || '').trim(),
        supplier: String(row['supplier'] || '').trim(),
        purchasePrice: row['purchaseprice'] ?? null,
        unitCost: row['unitcost'] ?? null,
        purchaseQuantity: row['purchasequantity'] ?? null,
        lowStockThreshold: row['lowstockthreshold'] ?? null
      }));
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);
      const suppliers = this.suppliers.map(s => s.companyName);
      await this.excelService.generateInventoryTemplate(categories, uoms, suppliers, dataRows);
    } catch (error) {
// $&
      this.showAlert('Error', 'Failed to export failed rows. Please try again.', 'bg-danger');
    }
  }


  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.processImportFile(file);
  }
  onProductFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.processImportFile(file);
  }
  async processImportFile(file: File): Promise<void> {
    this.isImporting = true;
    this.importProgress = 0;
    this.importResults = { success: 0, errors: [], warnings: [] };



    try {
      const items = await this.excelService.readInventoryFile(file);
      const batchSize = 10;
      let processedItems = 0;
      let importHalted = false;

      // Pre-validate all rows. Collect errors but proceed with valid rows.
      const validation = this.validateImportItems(items);
      if (validation.errors.length > 0) {
        this.importResults.errors.push(...validation.errors);
      }
      if (validation.warnings.length > 0) {
        this.importResults.warnings = [...(this.importResults.warnings || []), ...validation.warnings];
      }
      const invalidItems = new Set((validation.errors || []).map(e => e.item));
      const validItems = items.filter(item => !invalidItems.has(item));

      // If nothing valid to import, show errors and stop
      if (validItems.length === 0) {
        setTimeout(() => {
          this.isImporting = false;
          const errorModalElement = document.getElementById('errorDetailsModal');
          if (errorModalElement) {
            const errorModal = new bootstrap.Modal(errorModalElement);
            errorModal.show();
          }
        }, 300);
        return;
      }

      for (let i = 0; i < validItems.length; i += batchSize) {
        const batch = validItems.slice(i, i + batchSize);
        const results = await this.processBatch(batch);

        processedItems += batch.length;
        this.importProgress = Math.min(100, Math.round((processedItems / validItems.length) * 100));
        this.importResults.success += results.success;
        this.importResults.errors.push(...results.errors);

        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      this.showAlert('Success', 'Imported ' + this.importResults.success + ' items successfully', 'bg-success');
      // Close the modal after a slight delay to show 100%
      setTimeout(() => {
        this.isImporting = false;

        // Show error details if there were errors
        if (this.importResults.errors.length > 0) {
          const errorModalElement = document.getElementById('errorDetailsModal');
          if (errorModalElement) {
            const errorModal = new bootstrap.Modal(errorModalElement);
            errorModal.show();
          }
        }

        this.loadItems();
      }, 500);
// $&
    } catch (error) {
// $&
      this.isImporting = false;
      this.showAlert('Error', 'Failed to process import file. Please check the format and try again.', 'bg-danger');
    } finally {
      // Reset file input
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  private async processBatch(batch: any[]): Promise<{ success: number; errors: any[] }> {
// $&
    const results = { success: 0, errors: [] as any[] };
    const token = localStorage.getItem('authToken');

    if (!token) {
      //console.error('No auth token found');
      throw new Error('Authentication required');
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

// $&

    for (const [index, row] of batch.entries()) {
      try {
// $&

        // Normalize the row data by removing asterisks from property names and standardizing case
        const normalizedRow: any = {};
        Object.keys(row).forEach(key => {
          // Convert to camelCase and remove trailing asterisks
          const normalizedKey = key
            .replace(/\*+$/, '')  // Remove trailing asterisks
            .toLowerCase()
            .replace(/([-_][a-z])/g, group => group.toUpperCase().replace(/[-_]/g, ''));

          // Convert empty strings to null for required fields
          const value = row[key];
          normalizedRow[normalizedKey] = (value === '' || value === undefined) ? null : value;
        });

// $&

        // Skip only rows that are clearly instructions or examples
        const itemName = String(normalizedRow.itemName || '').trim();
        const lowerItemName = itemName.toLowerCase();

        // Only skip rows that are explicitly marked as examples/instructions
        const isInstructionRow = (
          lowerItemName === '' ||
          lowerItemName === 'example item' ||
          lowerItemName.startsWith('* required fields') ||
          lowerItemName.startsWith('instructions:') ||
          lowerItemName.startsWith('1. fill in the required fields') ||
          lowerItemName.startsWith('2. select values from dropdowns') ||
          lowerItemName.startsWith('3. do not modify the header') ||
          lowerItemName.startsWith('4. delete the example row') ||
          lowerItemName.startsWith('5. save as .xlsx file') ||
          lowerItemName.includes('example row') ||
          lowerItemName.includes('instruction')
        ) && (
            // Only skip if other required fields are also empty
            !normalizedRow.category &&
            !normalizedRow.unitofmeasure &&
            !normalizedRow.purchaseprice
          );

        if (isInstructionRow) {
// $&
          continue;
        }

// $&

        // Find required references using normalized row data
        const category = this.categories.find(c =>
          c.categoryName?.toLowerCase() === normalizedRow.category?.toLowerCase()
        );

        const uom = this.uomList.find(u =>
          u.uomName?.toLowerCase() === normalizedRow.unitofmeasure?.toLowerCase() ||
          u.uomName?.toLowerCase() === normalizedRow.uom?.toLowerCase() ||
          u.uomCode?.toLowerCase() === normalizedRow.unitofmeasure?.toLowerCase() ||
          u.uomCode?.toLowerCase() === normalizedRow.uom?.toLowerCase()
        );
        if (!category) {
          throw new Error(`Category not found: ${normalizedRow.category}`);
        }
        if (!uom) {
          throw new Error(`Unit of measure not found: ${normalizedRow.unitofmeasure || normalizedRow.uom}`);
        }

        // Map Excel column names to our expected field names
        const fieldMappings: { [key: string]: string } = {
          itemname: 'itemName',
          category: 'category',
          unitofmeasure: 'unitOfMeasure',
          uom: 'unitOfMeasure',
          purchaseprice: 'purchasePrice',
          unitcost: 'unitCost',
          purchasequantity: 'purchaseQuantity',
          lowstockthreshold: 'lowStockThreshold',
          sellingprice: 'sellingPrice',
          sku: 'itemCode'
        };

        // Map the normalized row data to our expected field names
        const mappedRow: any = {};
        Object.keys(normalizedRow).forEach(key => {
          const mappedKey = fieldMappings[key.toLowerCase()] || key;
          mappedRow[mappedKey] = normalizedRow[key];
        });

// $&

        // Ensure SKU is available by auto-generating if missing
        mappedRow.itemCode = String(mappedRow.itemCode || this.generateSKU(mappedRow.itemName));

        // Clean and validate monetary values
        this.sanitiseRowPrices(mappedRow);

        // Determine item type: Finished Good if sellingPrice is provided and purchase fields are absent,
        // or explicitly marked via itemType/itemTypeID
        const isFinishedGood = (
          (mappedRow.sellingPrice !== undefined && mappedRow.sellingPrice !== null) &&
          (mappedRow.purchasePrice === undefined && mappedRow.purchaseQuantity === undefined)
        ) || String(normalizedRow.itemtype || '').toLowerCase() === 'finished' || String(normalizedRow.itemtypeid || '').toLowerCase() === '2';

        // Validate required fields with proper field names
        const requiredFields = isFinishedGood
          ? ['itemName', 'category', 'unitOfMeasure', 'sellingPrice']
          : ['itemName', 'category', 'unitOfMeasure', 'purchasePrice', 'purchaseQuantity'];
        const missingFields = requiredFields.filter(field => {
          const value = mappedRow[field];
          return value === undefined || value === null || value === '';
        });

        if (missingFields.length > 0) {
          console.error('Required fields validation failed:', {
            row: normalizedRow,
            mappedRow,
            missingFields
          });
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        if (!category || !uom) {
          throw new Error(`Invalid category or UOM for item: ${row.itemName}`);
        }

        // isFinishedGood computed above

        // Prepare the item data matching saveItem structure
        // Parse numeric values with proper fallbacks
        const quantity = parseFloat(mappedRow.purchaseQuantity || '0');
        const purchasePrice = Number(mappedRow.purchasePrice) || 0;
        const unitCost = (() => {
          const provided = Number(mappedRow.unitCost);
          if (!isNaN(provided) && provided > 0) return provided;
          return quantity > 0 ? purchasePrice / quantity : 0;
        })();
        const sellingPrice = isFinishedGood ? (Number(mappedRow.sellingPrice) || 0) : 0;
        const lowStockThreshold = parseInt(mappedRow.lowStockThreshold || '0', 10);
        // Prepare the item data with mapped field names
        const itemData: any = {
          ItemID: 0, // New item
          ItemName: mappedRow.itemName,
          ItemCode: mappedRow.itemCode || this.generateSKU(mappedRow.itemName),

          itemTypeID: isFinishedGood ? 2 : 1,
          PurchaseUOMID: uom.uomid,
          lowStockThreshold: lowStockThreshold,
          isActive: true,
          valuationMethodCode: 'FIFO',
          categoryID: category.categoryId,
          profitMarginPercent: 0,
          quantity: quantity,
          quantityToAdd: quantity,
          purchasePrice: mappedRow.purchasePrice,
          unitCost: mappedRow.unitCost, // computed: unit cost = purchasePrice / purchaseQuantity when not provided
          sellingPrice: sellingPrice,
          // Ensure these are set based on item type
          purchaseQuantity: quantity, // Set purchase quantity to the imported quantity
          supplierID: !isFinishedGood ? (this.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())?.supplierID || 1) : 1
        };

        // Ensure we're using the correct case for the unit cost field
        itemData.purchasePrice = purchasePrice;      // total price
        itemData.unitCost = unitCost;                // per-unit
        itemData.purchaseQuantity = quantity;

// $&

        // Set prices based on item type
        if (isFinishedGood) {
          itemData.sellingPrice = Number(mappedRow.sellingPrice) || 0;
          itemData.purchasePrice = 0;
          itemData.unitCost = 0; // Finished goods don't have unit cost
        } else {

          // For raw materials, require supplier
          if (!this.suppliers.find(s => s.companyName?.toLowerCase() === String(normalizedRow.supplier || '').toLowerCase())) {
            throw new Error('Supplier is required for raw materials');
          }
        }

        // Save the item first
        let response;
        try {
          response = await this.http.post(
            `${environment.apiUrl}/api/Items/Add`,
            itemData,
            { headers, observe: 'response' as const }
          ).toPromise();
// $&

          // Create inventory transaction if quantity was added
          if (itemData.quantityToAdd > 0) {
            const savedItem = response?.body as any;
            if (savedItem) {
              // Calculate transaction unit cost - prioritize explicit unitCost, else derive from purchasePrice/purchaseQuantity
              const transactionUnitCost = Number(itemData.unitCost) > 0
              ? Number(itemData.unitCost)
              : (Number(itemData.purchaseQuantity) > 0
                  ? Number(itemData.purchasePrice) / Number(itemData.purchaseQuantity)
                  : 0);
              const transactionData = {
                itemID: savedItem.itemID || savedItem.ItemID, // Handle different response formats
                warehouseID: 1,
                transactionType: 'Receipt',
                quantityChange: parseFloat(itemData.quantityToAdd) || 0,
                costPerUnit: transactionUnitCost,
                transactionDate: new Date(),
                referenceType: 'Import',
                referenceID: `IMP-${Date.now()}`,
                userID: 1,
                notes: 'Added via Excel Import',
                batchNumber: null,
                expiryDate: null,
                lowStockThreshold: itemData.lowStockThreshold || 0
              };
              // Create the inventory transaction
              const transactionResponse = await this.http.post(
                `${environment.apiUrl}/api/InventoryTransactions/Add`,
                transactionData,
                { headers }
              ).toPromise();

// $&

              // Update the item's unit cost if this is a raw material
              if (!isFinishedGood && transactionUnitCost > 0) {
                const updateData = {
                  ...savedItem,
                  unitCost: transactionUnitCost,
                  purchasePrice: Number(itemData.purchasePrice), // keep total purchase price; do not overwrite with unit cost
                  // Ensure we're not sending read-only fields that might cause issues
                  itemID: undefined,
                  ItemID: savedItem.itemID || savedItem.ItemID
                };
                try {
                  const updateResponse = await this.http.put<any>(
                    `${environment.apiUrl}/api/Items/Update?ItemId=${savedItem.itemID || savedItem.ItemID}`,
                    updateData,
                    {
                      headers,
                      observe: 'response' as const
                    }
                  ).toPromise();

                  if (updateResponse) {
                  }
                } catch (updateError) {
// $&
                }
              }
            }
          }
        } catch (error) {
// $&
          throw error;
        }

        results.success++;
// $&
      } catch (error: any) {
        const errorMessage = error.error?.message || error.message || 'Unknown error';
// $&
        results.errors.push({
          item: row,
          itemName: row.itemName,
          rowIndex: row?.__excelRow ?? (index + 1),
          error: errorMessage
        });
// $&
      }
    }

// $&
    return results;
  }

  showErrorDetails(): void {
    const modalElement = document.getElementById('errorDetailsModal');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }



  inventoryTransactions: any[] = []; // Add this property

  // In your ngOnInit or loadItems method:
  loadTransactions() {
    this.http.get(`${environment.apiUrl}/api/InventoryTransactions`)
      .subscribe({
        next: (transactions: any) => {
          this.inventoryTransactions = transactions;
        },
        error: (error) => console.error('Error loading transactions', error)
      });

  }
  // In inventory-dashboard.component.ts
  calculateAverageCost(itemId: number): number {
    const transactions = this.inventoryTransactions.filter(t => t.itemID === itemId);
    if (!transactions.length) return 0;

    let totalCost = 0;
    let totalQuantity = 0;

    transactions.forEach(transaction => {
      if (transaction.transactionType === 'Receipt') {
        totalCost += transaction.quantityChange * transaction.costPerUnit;
        totalQuantity += transaction.quantityChange;
      }
    });

    return totalQuantity > 0 ? totalCost / totalQuantity : 0;
  }

  // Toggle table layout between side-by-side and stacked
  toggleTableLayout(): void {
    this.isTablesStacked = !this.isTablesStacked;

  }

  // Toggle purchase fields visibility in edit mode
togglePurchaseFields(): void {
  this.showPurchaseFields = !this.showPurchaseFields;

  if (this.showPurchaseFields && this.selectedItem) {
    // Set the default values to current item's values when showing fields
    this.inventoryForm.patchValue({
      quantityToAdd: this.selectedItem.quantity || 0, // Default to current quantity
      purchasePrice: this.selectedItem.purchasePrice || 0 // Default to current price
    });
  } else if (!this.showPurchaseFields) {
    // Clear fields when hiding
    this.inventoryForm.patchValue({
      purchasePrice: null,
      quantityToAdd: null
    });
  }
}
  editItem(item: EnhancedItemDTO): void {
    this.isEditing = true;
    this.selectedItem = item;
    this.isFinishedGood = item.itemTypeID === 2; // 2 is the ID for finished goods
    this.isUpdatingPurchasePrice = false; // Reset purchase price update mode
    this.showPurchaseFields = false; // Reset purchase fields visibility
    this.showAddModal = true;
    this.selectedOperations = [];

    // Ensure itemCode is properly set, generate one if it doesn't exist
    const itemCode = item.itemCode || this.generateSKU(item.itemName);

    // Reset form first to ensure all controls are in a clean state
    this.inventoryForm.reset();

    // Set all form values
    const formValues: any = {
      itemTypeID: item.itemTypeID,
      itemName: item.itemName,
      itemCode: itemCode,
      sellingPrice: item.sellingPrice || 0,

      PurchaseUOMID: item.purchaseUOMID,
      quantityToAdd: 0, // Don't allow changing quantity in edit mode
      currentQuantity: item.quantity || 0,
      lowStockThreshold: item.lowStockThreshold || 0,
      purchasePrice: item.purchasePrice || 0,
      profitMarginPercent: item.profitMarginPercent || 0,
      valuationMethodCode: item.valuationMethodCode || 'FIFO',
      categoryID: item.categoryID,
      supplierID: item.supplierID || null,
      isActive: item.isActive !== false // Default to true if not set
    };

    // Set unitCost if it exists, otherwise use purchasePrice for backward compatibility
    formValues.unitCost = item.unitCost !== undefined ? item.unitCost : item.purchasePrice || 0;

    this.inventoryForm.patchValue(formValues);

    // Set the selected category for the dropdown
    this.selectedCategory = this.categories.find(c => c.categoryId === item.categoryID) || null;

    // Set the selected supplier if exists
    if (item.supplierID) {
      this.selectedSupplier = this.suppliers.find(s => s.supplierID === item.supplierID) || null;
    }

    // Mark the form as untouched and pristine
    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
  }

  /**
   * Resets all form states to their default values
   */
  private resetFormStates(): void {
    this.isEditing = false;
    this.selectedItem = null;
    this.selectedSupplier = null;
    this.selectedCategory = null;
    this.selectedOperations = [];
    this.isAddingQuantity = false;
    this.isUpdatingPurchasePrice = false;
    this.showPurchaseFields = false;
    this.isCreatingSupplier = false;
    this.isCreatingCategory = false;

    // Reset all forms
    this.inventoryForm.reset();
    this.supplierForm.reset();
    this.categoryForm.reset();

    // Reset form values to default
    this.inventoryForm.patchValue({
      quantityToAdd: 1,
      currentQuantity: 0,
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      isActive: true,
      itemTypeID: 1, // Default to Raw Material
      valuationMethodCode: 'FIFO'
    });

    // Mark forms as untouched and pristine
    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
    this.supplierForm.markAsUntouched();
    this.supplierForm.markAsPristine();
    this.categoryForm.markAsUntouched();
    this.categoryForm.markAsPristine();
  }

  closeAddModal(): void {
    // Only reset the form if we're not in the middle of editing
    if (!this.isEditing) {
      this.resetFormStates();
      this.inventoryForm.reset();
    }

    // Close the modal
    this.showAddModal = false;

    // Clear selected item if not in edit mode
    if (!this.isEditing) {
      this.selectedItem = null;
    }
  }

  /**
   * Toggles the purchase price update mode for raw materials
   */
  togglePurchasePriceUpdate(): void {
    this.isUpdatingPurchasePrice = !this.isUpdatingPurchasePrice;
  }

  // New method to filter items
  filterItems(): void {
    if (!this.itemSearchTerm) {
      this.filteredItems = [...this.rawMaterials, ...this.finishedGoods];
      return;
    }

    const searchTerm = this.itemSearchTerm.toLowerCase();
    this.filteredItems = [...this.rawMaterials, ...this.finishedGoods].filter(item =>
      item.itemName.toLowerCase().includes(searchTerm) ||
      item.itemCode.toLowerCase().includes(searchTerm)
    );
  }

  filterRawMaterials(): void {
    const searchTerm = this.rawMaterialsSearchTerm.toLowerCase().trim();

    this.rawMaterials = this.originalRawMaterials.filter(item => {
      // Search term filter
      const matchesSearch = !searchTerm ||
        item.itemName?.toLowerCase().includes(searchTerm) ||
        item.itemCode?.toLowerCase().includes(searchTerm) ||
        this.getUOMName(item.purchaseUOMID)?.toLowerCase().includes(searchTerm) ||
        item.CategoryName?.toLowerCase().includes(searchTerm);

      // Category filter
      const matchesCategory = !this.selectedCategoryFilter ||
        item.categoryID === this.selectedCategoryFilter;

      // UOM filter
      const matchesUOM = !this.selectedUOMFilter ||
        item.purchaseUOMID === this.selectedUOMFilter;

      // Low stock filter
      const matchesLowStock = !this.showLowStockOnly ||
        (item.quantity !== undefined &&
          item.lowStockThreshold !== undefined &&
          item.quantity < item.lowStockThreshold);

      // Non-low stock filter
      const matchesNonLowStock = !this.showNonLowStockOnly ||
        (item.quantity !== undefined &&
          item.lowStockThreshold !== undefined &&
          item.quantity >= item.lowStockThreshold);



      return matchesSearch && matchesCategory && matchesUOM && matchesLowStock && matchesNonLowStock;
    });

    this.rawMaterialsCurrentPage = 1;
  }

  filterFinishedGoods(): void {
    const searchTerm = this.finishedGoodsSearchTerm.toLowerCase().trim();

    this.filteredFinishedGoods = this.originalFinishedGoods.filter(item => {
      // Search term filter
      const matchesSearch = !searchTerm ||
        item.itemName?.toLowerCase().includes(searchTerm) ||
        item.itemCode?.toLowerCase().includes(searchTerm) ||
        this.getUOMName(item.purchaseUOMID)?.toLowerCase().includes(searchTerm) ||
        item.CategoryName?.toLowerCase().includes(searchTerm);

      // Category filter
      const matchesCategory = !this.selectedCategoryFilter ||
        item.categoryID === this.selectedCategoryFilter;

        const qty = item.quantity ?? 0;

        const NoProductStock = !this.NoProductStock ||
        (qty === 0);

        const WithProductStock = !this.WithProductStock ||
        (qty !== 0);

      return matchesSearch && matchesCategory && NoProductStock && WithProductStock;
    });

    this.finishedGoodsCurrentPage = 1;
  }

  get rawMaterialsPaged(): EnhancedItemDTO[] {
    const start = (this.rawMaterialsCurrentPage - 1) * this.rawMaterialsPageSize;
    const end = start + this.rawMaterialsPageSize;
    return this.rawMaterials.slice(start, end);
  }

  get rawMaterialsTotalPages(): number {
    const length = this.rawMaterials ? this.rawMaterials.length : 0;
    const pages = Math.ceil(length / this.rawMaterialsPageSize) || 1;
    return Math.max(1, pages);
  }

  get rawMaterialsPages(): number[] {
    const total = this.rawMaterialsTotalPages;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = this.rawMaterialsCurrentPage - half;
    let end = this.rawMaterialsCurrentPage + half;

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  changeRawMaterialsPage(page: number): void {
    const total = this.rawMaterialsTotalPages;
    if (page < 1 || page > total) {
      return;
    }
    this.rawMaterialsCurrentPage = page;
  }

  get finishedGoodsPaged(): EnhancedItemDTO[] {
    const start = (this.finishedGoodsCurrentPage - 1) * this.finishedGoodsPageSize;
    const end = start + this.finishedGoodsPageSize;
    return this.filteredFinishedGoods.slice(start, end);
  }

  get finishedGoodsTotalPages(): number {
    const length = this.filteredFinishedGoods ? this.filteredFinishedGoods.length : 0;
    const pages = Math.ceil(length / this.finishedGoodsPageSize) || 1;
    return Math.max(1, pages);
  }

  get finishedGoodsPages(): number[] {
    const total = this.finishedGoodsTotalPages;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = this.finishedGoodsCurrentPage - half;
    let end = this.finishedGoodsCurrentPage + half;

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  changeFinishedGoodsPage(page: number): void {
    const total = this.finishedGoodsTotalPages;
    if (page < 1 || page > total) {
      return;
    }
    this.finishedGoodsCurrentPage = page;
  }

  addItemPriceHistory(itemID: number, purchasePrice: number, purchaseQuantity: number): void {
    const currentUserEmail = this.authService.getCurrentUserEmail();
    if (!this.selectedSupplier || !this.selectedSupplier.locations || this.selectedSupplier.locations.length === 0) {
// $&
      this.showAlert('Error', 'Supplier location is missing, cannot save price history.', 'bg-danger');
      return;
    }

    const priceHistoryEntry: ItemSupplierPriceHistoryDTO = {
      itemSupplierPriceID: 0,
      itemID: itemID,
      supplierLocationID: this.selectedSupplier.locations[0].locationID, // Assuming first location
      purchasePrice: purchasePrice,
      purchaseQuantity: purchaseQuantity,
      currencyCode: 'ZAR', // Or get from a config
      purchaseUOMID: this.inventoryForm.value.PurchaseUOMID,
      uomConversionFactor: 1, // Assuming base UOM
      supplierItemCode: this.inventoryForm.value.itemCode,
      leadTimeDays: 0, // Default value
      validFrom: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      createdBy: currentUserEmail || 'System'
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    });

    this.http.post(`${environment.apiUrl}/api/ItemSupplierPriceHistory/Add`, priceHistoryEntry, { headers })
      .subscribe({
        next: () => {
          this.showAlert('Success', 'Price history updated.', 'bg-success');
        },
        error: (error) => {
// $&
          this.showAlert('Error', 'Failed to update price history.', 'bg-danger');
        }
      });
  }



  isFilterPanelOpenRawMaterials = false;



  toggleFilterPanelRawMaterials() {
    this.isFilterPanelOpenRawMaterials = !this.isFilterPanelOpenRawMaterials;
  }

  isFilterPanelOpenFinishedGoods = false;

  toggleFilterPanelFinishedGoods() {
    this.isFilterPanelOpenFinishedGoods = !this.isFilterPanelOpenFinishedGoods;
  }
  resetFilters(): void {
    this.rawMaterialsSearchTerm = '';
    this.selectedCategoryFilter = null;
    this.selectedUOMFilter = null;
    this.showLowStockOnly = false;
    this.filterRawMaterials();
  }
  navigateToAddSupplier(): void {
    this.router.navigate(['/config'], {
      queryParams: {
        tab: 'suppliers',
        view: 'single'
      },
      state: { from: 'inventory' }
    });
  }

  // ── Mobile-specific handlers ─────────────────────────────────────────────────

  createCategoryMobile(categoryName: string): void {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });
    const newCat = { categoryId: 0, categoryName, createdAt: new Date() };
    this.http.post<any>(`${environment.apiUrl}/api/Category/Add`, newCat, { headers }).subscribe({
      next: (response) => {
        this.categories.push(response);
        this.loadItems();
      },
      error: () => this.toast.error('Failed to create category.', 3000),
    });
  }

  saveItemMobile(payload: any): void {
    if (payload.isAddingQuantity && payload.selectedItem) {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) { this.toast.error('Session expired. Please log in again.', 3000); return; }
      const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });
      const item = payload.selectedItem;
      const tx = {
        ItemID: item.itemID,
        WarehouseID: 1,
        TransactionType: 'Receipt',
        QuantityChange: Number(payload.itemData.quantityToAdd ?? 1),
        CostPerUnit: item.purchaseQuantity > 0 ? item.purchasePrice / item.purchaseQuantity : item.purchasePrice,
        TransactionDate: new Date().toISOString(),
        ReferenceType: 'Manual',
        ReferenceID: `ADD-${Date.now()}`,
        UserID: 1,
        Notes: 'Quantity added via Mobile',
        BatchNumber: null,
        ExpiryDate: null,
        ReferenceTypeCode: 'MANUAL',
      };
      this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).subscribe({
        next: () => {
          this.loadItems();
          this.toast.success('Quantity added successfully', 3000);
        },
        error: () => this.toast.error('Failed to add quantity. Please try again.', 3000),
      });
      return;
    }

    const { itemData, isEditing, mode, itemID } = payload;
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) { this.toast.error('Session expired. Please log in again.', 3000); return; }
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    const isProduct = mode === 'product';
    const body: any = {
      ItemID: isEditing && itemID ? itemID : 0,
      ItemName: itemData.itemName,
      ItemCode: itemData.itemCode || this.generateSKU(itemData.itemName),
      itemTypeID: isProduct ? 2 : 1,
      PurchaseUOMID: itemData.purchaseUOMID,
      lowStockThreshold: itemData.lowStockThreshold ?? 0,
      isActive: itemData.isActive ?? true,
      valuationMethodCode: 'FIFO',
      categoryID: itemData.categoryID,
      profitMarginPercent: 0,
      sellingPrice: isProduct ? (itemData.sellingPrice ?? 0) : 0,
      purchasePrice: isProduct ? 0 : (itemData.purchasePrice ?? 0),
      purchaseQuantity: isProduct ? 1 : (itemData.purchaseQuantity ?? 1),
      quantity: itemData.quantity ?? 0,
      unitCost: isProduct ? 0 : (itemData.unitCost ?? 0),
      supplierID: isProduct ? 1 : (itemData.supplierID ?? null),
      hasActualChanges: true,
    };

    const url = isEditing && itemID
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const method = isEditing ? 'put' : 'post';

    this.http.request<any>(method, url, { body, headers, observe: 'response' }).pipe(
      switchMap((resp: any) => {
        if (resp.status >= 400) throw new Error(`Server error ${resp.status}`);
        const savedId = isEditing && itemID ? itemID : resp.body?.itemID;
        if (!savedId) return of(null);
        // For edits, only post a transaction when price/qty actually changed
        const shouldPostTx = isEditing
          ? (itemData.hasPurchaseChange === true && (itemData.purchaseQuantity ?? 0) > 0)
          : true;
        if (!shouldPostTx) return of(savedId);
        const tx: InventoryTransactionDTO = {
          itemID: savedId,
          warehouseID: 1,
          transactionType: isEditing ? 'Adjustment' : 'Receipt',
          quantityChange: isEditing ? (itemData.purchaseQuantity ?? 0) : (itemData.quantity ?? 0),
          costPerUnit: itemData.unitCost ?? 0,
          lowStockThreshold: itemData.lowStockThreshold ?? 0,
          transactionDate: new Date(),
          referenceType: 'Manual Entry',
          referenceID: `UI-${Date.now()}`,
          userID: 1,
          notes: `${isEditing ? 'Updated' : 'Added'} via Mobile`,
          batchNumber: null,
          expiryDate: null,
        };
        return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).pipe(
          map(() => savedId),
          catchError(() => of(savedId))
        );
      }),
      catchError(err => {
        this.toast.error('Failed to save item. Please try again.', 3000);
        return of(null);
      })
    ).subscribe(savedId => {
      if (savedId !== null) {
        this.loadItems();
        this.toast.success(`Item ${isEditing ? 'updated' : 'added'} successfully`, 3000);
      }
    });
  }

  deleteItemMobile(event: { item: EnhancedItemDTO; mode: 'raw' | 'product' }): void {
    this.deleteItem(event.item);
  }

}
